/**
 * CaseDocumentsPanel
 *
 * Phase 4 (Module 2) — manage the documents filed under a single case/citation.
 * Mirrors the notes workflow: upload, list, view/download, and delete, all scoped to one ticket.
 *
 * Props:
 * - User_guid: the client the documents belong to
 * - Ticket_guid: the case/citation to file and list documents under
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  IconButton,
  Stack,
  Card,
  CardContent,
  Tooltip,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DescriptionIcon from '@mui/icons-material/Description';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import UserDocumentApi, {
  ACCEPTED_DOCUMENT_TYPES,
  MAX_BATCH_FILES,
  UserDocumentDto,
} from '../../api/UserDocumentApi';

interface CaseDocumentsPanelProps {
  User_guid: string;
  Ticket_guid: string;
}

const CaseDocumentsPanel: React.FC<CaseDocumentsPanelProps> = ({ User_guid, Ticket_guid }) => {
  const [documents, setDocuments] = useState<UserDocumentDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  // Files the server refused, listed by name so staff know exactly what to re-send.
  const [failures, setFailures] = useState<Array<{ name: string; error: string }>>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchDocuments = useCallback(async () => {
    if (!User_guid || !Ticket_guid) return;
    setLoading(true);
    setError(null);
    try {
      const docs = await UserDocumentApi.getUserDocuments(User_guid, Ticket_guid);
      setDocuments(docs);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [User_guid, Ticket_guid]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // Clear the success banner after a few seconds
  useEffect(() => {
    if (!success) return;
    const timer = setTimeout(() => setSuccess(null), 3000);
    return () => clearTimeout(timer);
  }, [success]);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFilesSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    // Reset the input so selecting the same files again still fires onChange
    event.target.value = '';
    if (files.length === 0) return;

    if (files.length > MAX_BATCH_FILES) {
      setError(`Select up to ${MAX_BATCH_FILES} files at a time.`);
      return;
    }

    setUploading(true);
    setError(null);
    setFailures([]);
    try {
      // File the whole batch under this case (Ticket_guid).
      const result = await UserDocumentApi.uploadDocuments(User_guid, files, Ticket_guid);

      setFailures(result.failed);
      if (result.uploadedCount > 0) {
        setSuccess(
          result.uploadedCount === 1 && files.length === 1
            ? `Uploaded "${files[0].name}"`
            : `Uploaded ${result.uploadedCount} of ${files.length} files`,
        );
      } else {
        setError('None of the selected files could be uploaded.');
      }
      await fetchDocuments();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload documents');
    } finally {
      setUploading(false);
    }
  };

  const handleView = async (doc: UserDocumentDto) => {
    setError(null);
    try {
      await UserDocumentApi.viewDocument(User_guid, doc);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to open document');
    }
  };

  const handleDelete = async (doc: UserDocumentDto) => {
    if (!window.confirm(`Delete "${doc.UserDocument_Name}"? This cannot be undone.`)) {
      return;
    }
    setError(null);
    try {
      await UserDocumentApi.deleteDocument(User_guid, doc.UserDocument_guid);
      setSuccess(`Deleted "${doc.UserDocument_Name}"`);
      await fetchDocuments();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete document');
    }
  };

  const handleToggleVisibility = async (doc: UserDocumentDto) => {
    const next = !(doc.UserDocument_ClientVisible ?? true);
    setError(null);
    try {
      await UserDocumentApi.setDocumentVisibility(User_guid, doc.UserDocument_guid, next);
      setSuccess(
        next
          ? `"${doc.UserDocument_Name}" is now visible to the client`
          : `"${doc.UserDocument_Name}" is now hidden from the client`,
      );
      await fetchDocuments();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update visibility');
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
          Documents ({documents.length})
        </Typography>
        <Button
          variant="contained"
          startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <UploadFileIcon />}
          onClick={handleUploadClick}
          disabled={uploading}
        >
          {uploading ? 'Uploading…' : 'Upload Documents'}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          hidden
          multiple
          accept={ACCEPTED_DOCUMENT_TYPES}
          onChange={handleFilesSelected}
        />
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Select several files at once (up to {MAX_BATCH_FILES}) — citation, driver&apos;s licence,
        insurance and court notices can all go up together. Files attach to this case and are
        visible to the client by default; use the eye icon to hide one. Accepted types: PDF, images,
        Word (max 50&nbsp;MB each).
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {failures.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setFailures([])}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            {failures.length} file{failures.length === 1 ? '' : 's'} could not be uploaded:
          </Typography>
          {failures.map((f) => (
            <Typography key={f.name} variant="caption" sx={{ display: 'block' }}>
              {f.name} — {f.error}
            </Typography>
          ))}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : documents.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', p: 3 }}>
          No documents attached to this case yet.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {documents.map((doc) => (
            <Card key={doc.UserDocument_guid} variant="outlined">
              <CardContent
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 1.5,
                  '&:last-child': { pb: 1.5 },
                }}
              >
                <DescriptionIcon color="action" />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, wordBreak: 'break-word' }}
                  >
                    {doc.UserDocument_Name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Uploaded {new Date(doc.UserDocument_CreatedAt).toLocaleString('en-US')}
                    {(doc.UserDocument_ClientVisible ?? true) ? '' : ' · Hidden from client'}
                  </Typography>
                </Box>
                <Tooltip
                  title={
                    (doc.UserDocument_ClientVisible ?? true)
                      ? 'Visible to client — click to hide'
                      : 'Hidden from client — click to show'
                  }
                >
                  <IconButton
                    size="small"
                    onClick={() => handleToggleVisibility(doc)}
                    aria-label="toggle client visibility"
                  >
                    {(doc.UserDocument_ClientVisible ?? true) ? (
                      <VisibilityIcon fontSize="small" color="primary" />
                    ) : (
                      <VisibilityOffIcon fontSize="small" color="disabled" />
                    )}
                  </IconButton>
                </Tooltip>
                <Button size="small" variant="outlined" onClick={() => handleView(doc)}>
                  View
                </Button>
                <Tooltip title="Delete">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDelete(doc)}
                    aria-label="delete document"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default CaseDocumentsPanel;
